import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const WFH_BASE_URL = `${API_BASE_PATH}/wfm`;

// ========== Types ==========

// 📝 POST payload
export interface WorkFromHomeRequest {
  no_of_days: "Full Day" | "Half Day" | "Custom";
  date_apply?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  session_type?: "Morning" | "Afternoon" | null;
  reason: string;
  notify: string;
}

// 🔁 PUT payload
export interface WorkFromHomeUpdate {
  wfm_id: number;
  status: "Approved" | "Rejected";
  reject_reason?: string;
  notify: string;
}

// 📥 Response record
export interface WorkFromHomeRecord {
  wfm_id: number;
  employee_id: string;
  date_apply: string | null;
  start_date: string | null;
  end_date: string | null;
  no_of_days: "Full Day" | "Half Day" | "Custom";
  session_type: "Morning" | "Afternoon" | null;
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason: string | null;
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
  updated_at: string | null;
}

// 📊 Widgets response
export interface WorkFromHomeWidgets {
  applied: number;
  approved: number;
  rejected: number;
  pending: number;
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// ========== API Calls ==========

// 🌟 POST - Create a WFH request
export const postWorkFromHome = async (
  data: WorkFromHomeRequest,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.post(`${WFH_BASE_URL}/`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Work From Home request submitted successfully");
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - Fetch own WFH records (Employee)
export const getSelfWorkFromHome = async (
  token: string | null
): Promise<WorkFromHomeRecord[]> => {
  try {
    const response = await axios.get(`${WFH_BASE_URL}/self/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - Fetch all WFH requests (Manager/Admin)
export const getAllWorkFromHome = async (
  token: string | null
): Promise<WorkFromHomeRecord[]> => {
  try {
    const response = await axios.get(`${WFH_BASE_URL}/all/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// ✅ PUT - Approve or Reject a WFH request
export const updateWorkFromHomeStatus = async (
  data: WorkFromHomeUpdate,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    formData.append("status", data.status);
    if (data.reject_reason) {
      formData.append("reject_reason", data.reject_reason);
    }

    const response = await axios.put(`${WFH_BASE_URL}/${data.wfm_id}`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success(`Work From Home ${data.status}`);
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 📊 GET - WFH Widgets
export const getWorkFromHomeWidgets = async (
  month: number,
  year: number,
  day: number | null,
  employee_id: string | null,
  token: string | null
): Promise<WorkFromHomeWidgets> => {
  try {
    const params = new URLSearchParams();
    params.append("month", month.toString());
    params.append("year", year.toString());
    if (day) params.append("day", day.toString());
    if (employee_id) params.append("employee_id", employee_id);

    const response = await axios.get(`${WFH_BASE_URL}/widgets?${params}`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};
